import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckSquare, Plus, Trash2, Edit2, Save, X, Calendar,
  RotateCcw, History, ChevronDown, ChevronUp, CheckCircle, XCircle,
  AlertCircle, List, Circle, Trophy, Zap, Users, Activity,
  Star, TrendingUp, Clock, Medal
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from './Modal';

interface ChecklistItem {
  id?: number;
  title: string;
  order_index?: number;
}

interface Checklist {
  id: number;
  user_id: number;
  title: string;
  description: string | null;
  frequency: 'daily' | 'weekly' | 'monthly';
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
  items: ChecklistItem[];
}

interface HistoryLog {
  id: number;
  checklist_id: number;
  checklist_title: string;
  frequency: string;
  log_date: string;
  is_completed: boolean;
  dismissed_at: string | null;
  item_logs: {
    id: number;
    checklist_item_id: number;
    item_title: string;
    is_completed: boolean;
  }[];
}

interface LeaderboardEntry {
  id: number;
  name: string;
  avatar: string | null;
  role: string;
  total_points: number;
  today_points: number;
  week_points: number;
}

interface ActivityEntry {
  user_id: number;
  user_name: string;
  user_avatar: string | null;
  checklist_title: string;
  item_title: string;
  completed_at: string;
  log_date: string;
}

interface AdminChecklist extends Checklist {
  owner_id: number;
  owner_name: string;
  owner_avatar: string | null;
  log_id: number | null;
  log_completed: boolean;
  item_logs: { daily_log_id: number; checklist_item_id: number; is_completed: boolean }[];
}

// FREQUENCY_LABELS is built dynamically inside the component using t()

const emptyForm = {
  title: '',
  description: '',
  frequency: 'daily' as 'daily' | 'weekly' | 'monthly',
  start_date: new Date().toISOString().slice(0, 10),
  end_date: '',
  items: [{ title: '' }],
  is_active: true,
};

type TodayChecks = Record<number, Record<number, boolean>>;

// Kullanıcı avatarı veya baş harf balonu
const UserAvatar: React.FC<{ name: string; avatar?: string | null; size?: 'sm' | 'md' | 'lg' }> = ({ name, avatar, size = 'md' }) => {
  const sizes = { sm: 'w-6 h-6 text-[10px]', md: 'w-8 h-8 text-xs', lg: 'w-10 h-10 text-sm' };
  if (avatar) {
    return <img src={avatar} alt={name} className={`${sizes[size]} rounded-full object-cover flex-shrink-0`} />;
  }
  const colors = ['bg-primary', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500', 'bg-cyan-500'];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className={`${sizes[size]} ${color} rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

// Sağ panel sekme tipleri
type RightTab = 'leaderboard' | 'activity' | 'others';

const ChecklistPage: React.FC = () => {
  const { t, language } = useLanguage();
  const FREQUENCY_LABELS: Record<string, string> = {
    daily: t('checklist.freqDaily'),
    weekly: t('checklist.freqWeekly'),
    monthly: t('checklist.freqMonthly'),
  };
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'list' | 'history'>('list');
  const [history, setHistory] = useState<HistoryLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [selectedChecklistFilter, setSelectedChecklistFilter] = useState<number | ''>('');
  const [todayChecks, setTodayChecks] = useState<TodayChecks>({});
  const [savingItem, setSavingItem] = useState<number | null>(null);
  const saveTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const today = new Date().toISOString().slice(0, 10);
  const { showNotification } = useNotifications();

  // Sağ panel state
  const [rightTab, setRightTab] = useState<RightTab>('leaderboard');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [adminChecklists, setAdminChecklists] = useState<AdminChecklist[]>([]);
  const [rightLoading, setRightLoading] = useState(false);
  const [expandedAdmin, setExpandedAdmin] = useState<number | null>(null);

  const loadChecklists = useCallback(async () => {
    try {
      const res = await apiService.getChecklists();
      setChecklists(res.checklists || []);
    } catch {
      showNotification(t('checklist.errLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTodayLogs = useCallback(async () => {
    try {
      const res = await apiService.getTodayChecklistLogs();
      const logs: any[] = res.logs || [];
      const checks: TodayChecks = {};
      logs.forEach(log => {
        checks[log.checklist_id] = {};
        (log.item_logs || []).forEach((il: any) => {
          checks[log.checklist_id][il.checklist_item_id] = !!il.is_completed;
        });
      });
      setTodayChecks(checks);
    } catch {
      // sessizce geç
    }
  }, []);

  const loadRightPanel = useCallback(async (rt: RightTab) => {
    setRightLoading(true);
    try {
      if (rt === 'leaderboard') {
        const data = await apiService.getChecklistLeaderboard();
        setLeaderboard(data || []);
      } else if (rt === 'activity') {
        const data = await apiService.getChecklistActivity(60);
        setActivity(data || []);
      } else if (rt === 'others') {
        const data = await apiService.getAllAdminChecklists();
        setAdminChecklists(data || []);
      }
    } catch {
      // sessizce geç
    } finally {
      setRightLoading(false);
    }
  }, []);

  const saveChecklistLog = useCallback(async (checklistId: number, checks: Record<number, boolean>, cl: Checklist) => {
    setSavingItem(checklistId);
    try {
      const itemCompletions = cl.items
        .filter(i => i.id !== undefined)
        .map(i => ({ item_id: i.id as number, is_completed: checks[i.id as number] || false }));
      const allDone = itemCompletions.every(ic => ic.is_completed);
      await apiService.saveChecklistLog({
        checklist_id: checklistId,
        log_date: today,
        is_completed: allDone,
        item_completions: itemCompletions,
      });
      // Aktivite ve liderlik panelini yenile
      if (rightTab === 'activity') loadRightPanel('activity');
      if (rightTab === 'leaderboard') loadRightPanel('leaderboard');
    } catch {
      // sessizce geç
    } finally {
      setSavingItem(null);
    }
  }, [today, rightTab, loadRightPanel]);

  const toggleTodayItem = (cl: Checklist, itemId: number) => {
    const clChecks = { ...(todayChecks[cl.id] || {}) };
    clChecks[itemId] = !clChecks[itemId];
    setTodayChecks(prev => ({ ...prev, [cl.id]: clChecks }));
    if (saveTimers.current[cl.id]) clearTimeout(saveTimers.current[cl.id]);
    saveTimers.current[cl.id] = setTimeout(() => {
      saveChecklistLog(cl.id, clChecks, cl);
    }, 600);
  };

  const loadHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await apiService.getChecklistHistory({
        page,
        limit: 20,
        checklist_id: selectedChecklistFilter || undefined,
      });
      setHistory(res.logs || []);
      setHistoryTotal(res.pagination?.total || 0);
    } catch {
      showNotification(t('checklist.errHistoryLoad'), 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedChecklistFilter]);

  useEffect(() => {
    loadChecklists();
    loadTodayLogs();
    loadRightPanel('leaderboard');
  }, [loadChecklists, loadTodayLogs, loadRightPanel]);

  useEffect(() => {
    if (tab === 'history') loadHistory(historyPage);
  }, [tab, historyPage, loadHistory]);

  useEffect(() => {
    loadRightPanel(rightTab);
  }, [rightTab]);

  // Aktivite paneli otomatik yenileme (60s)
  useEffect(() => {
    if (rightTab !== 'activity') return;
    const interval = setInterval(() => loadRightPanel('activity'), 60000);
    return () => clearInterval(interval);
  }, [rightTab, loadRightPanel]);

  const openCreate = () => { setEditingChecklist(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (cl: Checklist) => {
    setEditingChecklist(cl);
    setForm({
      title: cl.title,
      description: cl.description || '',
      frequency: cl.frequency,
      start_date: cl.start_date.slice(0, 10),
      end_date: cl.end_date ? cl.end_date.slice(0, 10) : '',
      items: cl.items.length > 0 ? cl.items.map(i => ({ title: i.title, id: i.id })) : [{ title: '' }],
      is_active: cl.is_active,
    });
    setShowModal(true);
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { title: '' }] }));
  const removeItem = (i: number) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i: number, title: string) => setForm(f => ({ ...f, items: f.items.map((item, idx) => idx === i ? { ...item, title } : item) }));

  const handleSave = async () => {
    if (!form.title.trim()) { showNotification(t('checklist.errTitleRequired'), 'error'); return; }
    const validItems = form.items.filter(i => i.title.trim());
    if (validItems.length === 0) { showNotification(t('checklist.errItemRequired'), 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        frequency: form.frequency,
        start_date: form.start_date,
        end_date: form.end_date || null,
        is_active: form.is_active,
        items: validItems.map(i => ({ title: i.title.trim() })),
      };
      if (editingChecklist) {
        await apiService.updateChecklist(editingChecklist.id, payload);
        showNotification(t('checklist.successUpdated'), 'success');
      } else {
        await apiService.createChecklist(payload);
        showNotification(t('checklist.successCreated'), 'success');
      }
      setShowModal(false);
      loadChecklists();
    } catch (error: any) {
      showNotification(error.message || t('checklist.errSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('checklist.confirmDelete'))) return;
    try {
      await apiService.deleteChecklist(id);
      showNotification(t('checklist.successDeleted'), 'success');
      loadChecklists();
    } catch {
      showNotification(t('checklist.errDelete'), 'error');
    }
  };

  const toggleActive = async (cl: Checklist) => {
    try {
      await apiService.updateChecklist(cl.id, {
        title: cl.title,
        description: cl.description,
        frequency: cl.frequency,
        start_date: cl.start_date.slice(0, 10),
        end_date: cl.end_date ? cl.end_date.slice(0, 10) : null,
        is_active: !cl.is_active,
        items: cl.items.map(i => ({ title: i.title })),
      });
      loadChecklists();
    } catch {
      showNotification(t('checklist.errUpdate'), 'error');
    }
  };

  const totalPages = Math.ceil(historyTotal / 20);

  // Admin checklists: kullanıcıya göre grupla
  const adminGroups = adminChecklists.reduce<Record<number, { name: string; avatar: string | null; items: AdminChecklist[] }>>((acc, cl) => {
    if (!acc[cl.owner_id]) acc[cl.owner_id] = { name: cl.owner_name, avatar: cl.owner_avatar, items: [] };
    acc[cl.owner_id].items.push(cl);
    return acc;
  }, {});

  // Aktivite için zaman formatlama
  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('checklist.justNow');
    if (mins < 60) return `${mins}${t('checklist.minutesAgo')}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}${t('checklist.hoursAgo')}`;
    return new Date(dateStr).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', { day: 'numeric', month: 'short' });
  };

  // Rozet (rank) hesapla
  const getRankBadge = (index: number) => {
    if (index === 0) return { icon: <Trophy className="w-4 h-4 text-yellow-500" />, bg: 'bg-yellow-50 dark:bg-yellow-900/20' };
    if (index === 1) return { icon: <Medal className="w-4 h-4 text-gray-400" />, bg: 'bg-gray-50 dark:bg-gray-700/50' };
    if (index === 2) return { icon: <Medal className="w-4 h-4 text-orange-400" />, bg: 'bg-orange-50 dark:bg-orange-900/20' };
    return { icon: <span className="text-xs font-bold text-gray-400 w-4 text-center">{index + 1}</span>, bg: '' };
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <CheckSquare className="w-7 h-7 text-primary" />
            {t('checklist.pageTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('checklist.pageSubtitle')}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          {t('checklist.newChecklist')}
        </button>
      </div>

      {/* İki sütunlu layout */}
      <div className="flex gap-5 items-start">

        {/* ── Sol: Mevcut içerik ── */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setTab('list')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'list' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:text-gray-400'
              }`}
            >
              <List className="w-4 h-4" />{t('checklist.tabMyChecklists')}
            </button>
            <button
              onClick={() => setTab('history')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'history' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:text-gray-400'
              }`}
            >
              <History className="w-4 h-4" />{t('checklist.tabHistory')}
            </button>
          </div>

          {/* List Tab */}
          {tab === 'list' && (
            <div>
              {loading ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('checklist.loading')}</div>
              ) : checklists.length === 0 ? (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                  <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">{t('checklist.empty')}</p>
                  <p className="text-sm mt-1">{t('checklist.emptyHint')}</p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {checklists.map(cl => (
                    <div
                      key={cl.id}
                      className={`bg-white dark:bg-gray-800 rounded-xl border ${
                        cl.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-gray-100 dark:border-gray-800 opacity-60'
                      } p-4 shadow-sm space-y-3`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              cl.frequency === 'daily' ? 'bg-green-100 text-green-700' :
                              cl.frequency === 'weekly' ? 'bg-primary/20 text-primary-dark' : 'bg-purple-100 text-purple-700'
                            }`}>
                              <RotateCcw className="w-3 h-3 mr-1" />
                              {FREQUENCY_LABELS[cl.frequency]}
                            </span>
                            {!cl.is_active && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{t('checklist.inactive')}</span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mt-1 truncate">{cl.title}</h3>
                          {cl.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{cl.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openEdit(cl)} className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-primary/10 transition-colors" title={t('checklist.editTitle')}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(cl.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors" title={t('checklist.deleteTitle')}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(cl.start_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        {cl.end_date && ` – ${new Date(cl.end_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}`}
                      </div>

                      {cl.is_active && cl.items.length > 0 && (() => {
                        const checks = todayChecks[cl.id] || {};
                        const done = cl.items.filter(i => i.id && checks[i.id]).length;
                        const total = cl.items.length;
                        return (
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-400">{t('checklist.today')}</span>
                              <span className={`text-xs font-medium ${done === total ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'}`}>{done}/{total}</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1">
                              <div
                                className={`h-1 rounded-full transition-all duration-300 ${done === total ? 'bg-green-500' : 'bg-primary'}`}
                                style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        );
                      })()}

                      <div className="space-y-1">
                        {cl.items.map(item => {
                          const isChecked = item.id ? (todayChecks[cl.id]?.[item.id] || false) : false;
                          return (
                            <button
                              key={item.id}
                              onClick={() => item.id && cl.is_active && toggleTodayItem(cl, item.id)}
                              disabled={!cl.is_active || !item.id}
                              className={`w-full flex items-center gap-2 text-sm rounded-lg px-2 py-1 transition-colors text-left
                                ${cl.is_active && item.id
                                  ? isChecked
                                    ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    : 'text-gray-600 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700/50'
                                  : 'text-gray-400 cursor-default'
                                }`}
                            >
                              {isChecked
                                ? <CheckCircle className="w-4 h-4 shrink-0 text-green-500" />
                                : <Circle className="w-4 h-4 shrink-0 text-gray-300 dark:text-gray-600 dark:text-gray-400" />
                              }
                              <span className={`truncate ${isChecked ? 'line-through opacity-70' : ''}`}>{item.title}</span>
                              {savingItem === cl.id && <span className="ml-auto text-xs text-gray-300 shrink-0">●</span>}
                            </button>
                          );
                        })}
                      </div>

                      <div className="pt-1 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                        <button
                          onClick={() => toggleActive(cl)}
                          className={`text-xs font-medium transition-colors ${cl.is_active ? 'text-gray-400 hover:text-orange-500' : 'text-gray-400 hover:text-green-500'}`}
                        >
                          {cl.is_active ? t('checklist.deactivate') : t('checklist.activate')}
                        </button>
                        {savingItem === cl.id && <span className="text-xs text-primary">{t('checklist.saving')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {tab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <select
                  value={selectedChecklistFilter}
                  onChange={e => { setSelectedChecklistFilter(e.target.value ? Number(e.target.value) : ''); setHistoryPage(1); }}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">{t('checklist.filterAll')}</option>
                  {checklists.map(cl => <option key={cl.id} value={cl.id}>{cl.title}</option>)}
                </select>
              </div>

              {historyLoading ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('checklist.loading')}</div>
              ) : history.length === 0 ? (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">{t('checklist.historyEmpty')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map(log => (
                    <div key={log.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-750"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <div className="flex items-center gap-3">
                          {log.is_completed
                            ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                            : <XCircle className="w-5 h-5 text-red-400 shrink-0" />
                          }
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{log.checklist_title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(log.log_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                log.frequency === 'daily' ? 'bg-green-100 text-green-700' :
                                log.frequency === 'weekly' ? 'bg-primary/20 text-primary-dark' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {FREQUENCY_LABELS[log.frequency]}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${log.is_completed ? 'text-green-600' : 'text-red-500'}`}>
                            {log.is_completed ? t('checklist.completed') : t('checklist.notCompleted')}
                          </span>
                          {expandedLog === log.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>
                      {expandedLog === log.id && log.item_logs.length > 0 && (
                        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-2">
                          {log.item_logs.map(il => (
                            <div key={il.id} className="flex items-center gap-2 text-sm">
                              {il.is_completed
                                ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                                : <AlertCircle className="w-4 h-4 text-gray-300 shrink-0" />
                              }
                              <span className={il.is_completed ? 'text-gray-600 dark:text-gray-400 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>
                                {il.item_title}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 pt-2">
                      <button onClick={() => setHistoryPage(p => Math.max(1, p - 1))} disabled={historyPage === 1}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                        {t('checklist.prevPage')}
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{historyPage} / {totalPages}</span>
                      <button onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))} disabled={historyPage === totalPages}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                        {t('checklist.nextPage')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Sağ Panel ── */}
        <div className="hidden lg:flex flex-col w-80 xl:w-96 shrink-0 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {/* Sağ panel sekmeleri */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {([
              { id: 'leaderboard' as RightTab, label: t('checklist.rightTabLeaderboard'), icon: <Trophy className="w-3.5 h-3.5" /> },
              { id: 'activity' as RightTab, label: t('checklist.rightTabActivity'), icon: <Activity className="w-3.5 h-3.5" /> },
              { id: 'others' as RightTab, label: t('checklist.rightTabOthers'), icon: <Users className="w-3.5 h-3.5" /> },
            ] as const).map(tab => (
              <button
                key={tab.id}
                onClick={() => setRightTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold border-b-2 transition-colors ${
                  rightTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>

          {/* Panel içeriği */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
            {rightLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2" />
                <span className="text-sm">{t('checklist.loading')}</span>
              </div>
            ) : (

              /* ── Liderlik Tablosu ── */
              rightTab === 'leaderboard' && (
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('checklist.leaderboardTitle')}</span>
                  </div>
                  {leaderboard.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 text-sm">{t('checklist.leaderboardEmpty')}</div>
                  ) : leaderboard.map((entry, idx) => {
                    const { icon, bg } = getRankBadge(idx);
                    return (
                      <div key={entry.id} className={`flex items-center gap-3 p-3 rounded-xl ${bg || 'hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/50'} transition-colors`}>
                        <div className="flex items-center justify-center w-5 shrink-0">{icon}</div>
                        <UserAvatar name={entry.name} avatar={entry.avatar} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{entry.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400">{t('checklist.leaderboardThisWeek')} <span className="text-primary font-medium">{entry.week_points}</span></span>
                            {entry.today_points > 0 && (
                              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full font-medium">
                                +{entry.today_points} {t('checklist.leaderboardToday')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-lg font-bold text-gray-800 dark:text-gray-200 dark:text-gray-100">{entry.total_points}</div>
                          <div className="text-[10px] text-gray-400">{t('checklist.leaderboardPoints')}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-400 text-center">{t('checklist.leaderboardFooter')} <span className="font-semibold text-primary">+1 {t('checklist.leaderboardPoints')}</span></p>
                  </div>
                </div>
              )
            )}

            {/* ── Aktivite Akışı ── */}
            {!rightLoading && rightTab === 'activity' && (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-yellow-500" />{t('checklist.activityTitle')}
                  </span>
                  <button onClick={() => loadRightPanel('activity')} className="text-xs text-primary hover:text-primary-dark">{t('checklist.activityRefresh')}</button>
                </div>
                {activity.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm px-4">{t('checklist.activityEmpty')}</div>
                ) : activity.map((act, idx) => (
                  <div key={idx} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/30 transition-colors">
                    <UserAvatar name={act.user_name} avatar={act.user_avatar} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{act.user_name}</span>
                        {' '}<span className="text-gray-500 dark:text-gray-400">{t('checklist.activityCompleted')}</span>{' '}
                        <span className="font-medium text-primary dark:text-primary">{act.item_title}</span>
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">{act.checklist_title}</p>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">{timeAgo(act.completed_at)}</span>
                      <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full font-semibold">{t('checklist.activityPoint')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Diğer Adminlerin Checklists ── */}
            {!rightLoading && rightTab === 'others' && (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('checklist.othersTitle')}</span>
                </div>
                {Object.keys(adminGroups).length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">{t('checklist.othersEmpty')}</div>
                ) : Object.entries(adminGroups).map(([ownerId, group]) => {
                  const id = Number(ownerId);
                  const totalItems = group.items.reduce((s, cl) => s + cl.items.length, 0);
                  const doneItems = group.items.reduce((s, cl) => {
                    const logMap = Object.fromEntries((cl.item_logs || []).map(il => [il.checklist_item_id, il.is_completed]));
                    return s + cl.items.filter(i => i.id && logMap[i.id]).length;
                  }, 0);
                  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;
                  const isExpanded = expandedAdmin === id;

                  return (
                    <div key={ownerId} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                      <button
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/50 transition-colors text-left"
                        onClick={() => setExpandedAdmin(isExpanded ? null : id)}
                      >
                        <UserAvatar name={group.name} avatar={group.avatar} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{group.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-300 ${pct === 100 ? 'bg-green-500' : 'bg-primary'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={`text-xs font-medium shrink-0 ${pct === 100 ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'}`}>
                              {doneItems}/{totalItems}
                            </span>
                          </div>
                        </div>
                        <div className="shrink-0">
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50">
                          {group.items.map(cl => {
                            const logMap = Object.fromEntries((cl.item_logs || []).map(il => [il.checklist_item_id, il.is_completed]));
                            const clDone = cl.items.filter(i => i.id && logMap[i.id]).length;
                            return (
                              <div key={cl.id} className="px-3 py-2.5">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate pr-2">{cl.title}</span>
                                  <span className={`text-xs shrink-0 ${clDone === cl.items.length ? 'text-green-600' : 'text-gray-400'}`}>
                                    {clDone}/{cl.items.length}
                                  </span>
                                </div>
                                <div className="space-y-0.5">
                                  {cl.items.map(item => {
                                    const done = item.id ? !!logMap[item.id] : false;
                                    return (
                                      <div key={item.id} className="flex items-center gap-1.5 text-xs">
                                        {done
                                          ? <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                                          : <Circle className="w-3 h-3 text-gray-300 dark:text-gray-600 dark:text-gray-400 shrink-0" />
                                        }
                                        <span className={`truncate ${done ? 'text-gray-400 line-through' : 'text-gray-600 dark:text-gray-400 dark:text-gray-300'}`}>
                                          {item.title}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} zIndex="z-[70]">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {editingChecklist ? t('checklist.modalEditTitle') : t('checklist.modalCreateTitle')}
            </h2>
            <button onClick={() => setShowModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('checklist.labelTitle')} <span className="text-red-500">*</span></label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder={t('checklist.titlePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('checklist.labelDescription')}</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder={t('checklist.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('checklist.labelFrequency')} <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-3 gap-2">
                {(['daily', 'weekly', 'monthly'] as const).map(freq => (
                  <button key={freq} type="button" onClick={() => setForm(f => ({ ...f, frequency: freq }))}
                    className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      form.frequency === freq ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary'
                    }`}>
                    {FREQUENCY_LABELS[freq]}
                  </button>
                ))}
              </div>
              {form.frequency === 'weekly' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('checklist.frequencyWeeklyHint')} ({form.start_date ? new Date(form.start_date + 'T12:00:00').toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', { weekday: 'long' }) : '—'})
                </p>
              )}
              {form.frequency === 'monthly' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('checklist.frequencyMonthlyHint')} ({form.start_date ? new Date(form.start_date + 'T12:00:00').getDate() + t('checklist.frequencyMonthlyDay') : '—'})
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('checklist.labelStartDate')} <span className="text-red-500">*</span></label>
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('checklist.labelEndDate')}</label>
                <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  min={form.start_date}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('checklist.labelItems')} <span className="text-red-500">*</span></label>
                <button type="button" onClick={addItem} className="text-xs text-primary hover:text-primary-dark flex items-center gap-1 font-medium">
                  <Plus className="w-3.5 h-3.5" />{t('checklist.addItem')}
                </button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-5 text-right shrink-0">{i + 1}.</span>
                    <input type="text" value={item.title} onChange={e => updateItem(i, e.target.value)}
                      placeholder={`${t('checklist.itemPlaceholder')} ${i + 1}`}
                      className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary" />
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} className="p-1.5 text-gray-400 hover:text-red-500 rounded">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
            <button onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg transition-colors">
              {t('checklist.btnCancel')}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50">
              <Save className="w-4 h-4" />
              {saving ? t('checklist.saving') : t('checklist.btnSave')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChecklistPage;
